import React, { useState, useContext, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Alert,
  Spinner,
} from "react-bootstrap";
import {
  FaMapMarkerAlt,
  FaEnvelope,
  FaPhoneAlt,
  FaWhatsapp,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import "./home.css";
import { CartContext } from "./CartContext";
import PaginationComponent from "../components/Forms";
import config from "../config/config";

const Home = () => {
  const { API_DOMAIN, subdomain } = config;
  const { cart, addToCart, increaseQty, decreaseQty, removeItem } =
    useContext(CartContext);

  // ------------------ COMPANY DETAILS STATE --------------------
  const [company, setCompany] = useState(null);
  const [companyLoading, setCompanyLoading] = useState(true);
  const [companyError, setCompanyError] = useState(null);
  // console.log("Company :", company);
  // ------------------ PRODUCTS & CATEGORY STATE ----------------
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const itemsPerPage = 100;
  const navigate = useNavigate();

  // ------------------ FETCH COMPANY DETAILS --------------------
  useEffect(() => {
    const fetchCompany = async () => {
      setCompanyLoading(true);
      setCompanyError(null);

      try {
        const payload = { search_text: "company", company_id: subdomain };
        const url = `${API_DOMAIN}/company.php`;

        const response = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        // console.log("Company fetch payload:", payload);
        if (!response.ok) throw new Error("Failed to fetch company details");

        const data = await response.json();

        // console.log("Company fetch response data:", data);

        if (data.head.code === 200) {

          setCompany(data.body.companies[0]);
        } else {
          throw new Error(data.head.msg);
        }
      } catch (err) {
        setCompanyError(err.message);
      } finally {
        setCompanyLoading(false);
      }
    };

    fetchCompany();
  }, []);

  // ------------------ FETCH PRODUCTS + CATEGORIES --------------
  useEffect(() => {
    const fetchProductsAndCategories = async () => {
      setLoading(true);
      setError(null);

      const companyId = subdomain;
      const payload = { company_id: companyId, fetch_all: "true" };

      try {
        // 1. CATEGORY FETCH
        const categoryResponse = await fetch(`${API_DOMAIN}/category.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        const categoryData = await categoryResponse.json();

        let fetchedCategories = [{ id: "all", name: "All" }];
        if (categoryData.head.code === 200) {
          const apiCats = categoryData.body.categories.map((cat) => ({
            id: String(cat.category_code),
            name: cat.category_name,
          }));
          fetchedCategories = [...fetchedCategories, ...apiCats];
        }
        setCategories(fetchedCategories);

        // 2. PRODUCT FETCH
        const productResponse = await fetch(`${API_DOMAIN}/product.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        const productData = await productResponse.json();

        if (productData.head.code === 200) {
          let fetchedProducts = productData.body.products.map((product) => ({
            id: product.id,
            product_id: product.product_id,
            product_name: product.product_name,
            product_code: product.product_code,
            category_code: String(product.category_code),
            price: parseFloat(product.product_price) || 0,
            product_img_url: product.product_img_url || "",
          }));
          setProducts(fetchedProducts);
        } else {
          throw new Error(productData.head.msg);
        }
      } catch (err) {
        setError("Could not load products/categories — " + err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProductsAndCategories();
  }, []);

  // ------------------ FILTERING ------------------
  const filteredProducts = products.filter(
    (p) =>
      (selectedCategory === "all" || p.category_code === selectedCategory) &&
      p.product_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // ------------------ PAGINATION ------------------
  const totalItems = filteredProducts.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const currentProducts = filteredProducts.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (p) => setCurrentPage(p);

  const cartTotalItems = cart.reduce((sum, i) => sum + i.qty, 0);
  const cartTotalPrice = cart.reduce((sum, i) => sum + i.price * i.qty, 0);

  const handleProductClick = (product) => {
    navigate("/product-details", { state: { product } });
  };

  // ----------------------------------------------------
  return (
    <div className="home-section overflow-hidden">
      <Container>
        {/* ------------- COMPANY DETAILS ------------- */}
        {companyLoading ? (
          <p className="text-center">Loading company details...</p>
        ) : companyError ? (
          <Alert variant="danger">Error: {companyError}</Alert>
        ) : company ? (
          <>
            <Row className="align-items-start justify-content-center mb-4">
              <Col xs={12} md={4} className="company-logo-col">
                <h2 className="home-title">{company.company_name}</h2>

                <p className="home-info">
                  <FaMapMarkerAlt className="icon" />
                  {company.address?.split("\n").map((line, i) => (
                    <span key={i}>{line}<br /></span>
                  ))}
                </p>


                <p className="home-info">
                  <FaEnvelope className="icon" /> {company.city},{" "}
                  {company.state}
                </p>

                <p className="home-info">
                  Contact: {company.mobile}, {company.phone}
                </p>

                <button className="home-call-btn d-none d-lg-inline-flex d-md-inline-flex">
                  <FaPhoneAlt className="icon" />
                  <span>{company.mobile}</span>
                </button>
              </Col>
              <Col xs={12} md={3} className="d-flex justify-content-center">
                <img
                  src={company.img || "https://zenonlinestore.zentexus.in/uploads/company/2025-12-15-13-39-50.webp"}
                  alt="Company Logo"
                  className="home-logo"
                />
              </Col>
            </Row>

            {/* Mobile buttons - now inside conditional */}
            <Row className="mt-2 d-flex d-md-none">
              <Col xs={6} className="d-flex justify-content-center">
                <a
                  href={`tel:${company.mobile}`}
                  className="home-call-btn w-100"
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    textDecoration: "none",
                  }}
                >
                  <FaPhoneAlt className="icon" />
                  <span>Call Store</span>
                </a>
              </Col>

              <Col xs={6} className="d-flex justify-content-center">
                {company.mobile && (
                  <a
                    href={`https://wa.me/${String(company.mobile).replace(
                      /[^0-9]/g,
                      ""
                    )}`}
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      backgroundColor: "#25D366",
                      color: "white",
                      textDecoration: "none",
                      borderRadius: "5px",
                      padding: "0px",
                    }}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="home-call-btn w-100"
                  >
                    <FaWhatsapp className="icon" />
                    <span className="ms-1">WhatsApp</span>
                  </a>
                )}
              </Col>
            </Row>
          </>
        ) : null}

        <hr />

        {/* ----------- SEARCH BAR ----------- */}
        <Row className="mb-3">
          <Col xs={12} md={12}>
            <Form.Control
              type="text"
              placeholder="Search for an item"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </Col>
        </Row>

        {/* ----------- CATEGORY BUTTONS ----------- */}
        <Row className="mb-4">
          <div className="category-scroll-container">
            {categories.map((cat) => (
              <Button
                key={cat.id}
                variant={
                  selectedCategory === cat.id ? "danger" : "outline-secondary"
                }
                className="category-btn"
                onClick={() => {
                  setSelectedCategory(cat.id);
                  setCurrentPage(1);
                }}
              >
                {cat.name}
              </Button>
            ))}
          </div>
        </Row>

        {/* ----------- PRODUCT LOADING / ERROR ----------- */}
        {loading && (
          <div className="text-center my-5">
            <Spinner animation="border" />
            <p>Loading products…</p>
          </div>
        )}

        {error && <Alert variant="danger">{error}</Alert>}

        {/* ----------- PRODUCT GRID ----------- */}
        <Row className="product-grid">
          {!loading &&
            !error &&
            currentProducts.map((prod) => {
              const cartItem = cart.find((i) => i.id === prod.id);
              return (
                <Col
                  key={prod.id}
                  xs={6}
                  sm={4}
                  md={3}
                  lg={2}
                  className="mb-4 custom-col"
                >
                  <div
                    className="product-card"
                    onClick={() => handleProductClick(prod)}
                  >
                    <img
                      src={prod.product_img_url || "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcT7r0TBgU8-SuHKlgfTmNzdQoMrk3nU5tFarg&s"}
                      alt={prod.product_name}
                      className="product-img"
                    />
                    <div className="product-details">
                      <p className="product-name">{prod.product_name}</p>
                      <p className="product-code">
                        ITEM CODE: {prod.product_code}
                      </p>

                      <div className="d-flex justify-content-between">
                        <p className="product-price">₹ {prod.price}</p>

                        {cartItem ? (
                          <div className="quantity-controls">
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              onClick={(e) => {
                                e.stopPropagation();
                                cartItem.qty === 1
                                  ? removeItem(prod.id)
                                  : decreaseQty(prod.id);
                              }}
                            >
                              −
                            </Button>
                            <span className="mx-2">{cartItem.qty}</span>
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              onClick={(e) => {
                                e.stopPropagation();
                                increaseQty(prod.id);
                              }}
                            >
                              +
                            </Button>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              addToCart(prod);
                            }}
                          >
                            + Add
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </Col>
              );
            })}
        </Row>

        {/* ----------- PAGINATION ----------- */}
        {totalItems > itemsPerPage && (
          <Row className="justify-content-center mt-4">
            <PaginationComponent
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </Row>
        )}

        {/* ----------- CART BAR ----------- */}
        {cart.length > 0 && (
          <div className="cart-bar">
            <div
              className="cart-info-clickable"
              onClick={() => navigate("/orderpage")}
              style={{ cursor: "pointer" }}
            >
              <span>{cartTotalItems} ITEMS</span>
              <span className="separator"> . </span>
              <span>₹ {cartTotalPrice.toFixed(2)}</span>
            </div>
            <div className="cart-action position-relative">
              <span
                className="cart-icon"
                onClick={() => navigate("/orderpage")}
                style={{ cursor: "pointer" }}
              >
                🛒
              </span>

              {cartTotalItems > 0 && (
                <span className="cart-badge">{cartTotalItems}</span>
              )}

              <Button onClick={() => navigate("/orderpage")}>View cart</Button>
            </div>
          </div>
        )}
      </Container>
    </div>
  );
};

export default Home;
